import React from 'react';
import moment from 'moment';
import { AccessRightDTO } from '@peartherapeutics/hylia-api-client';
import Select from 'components/common/Select';
import { PRESCRIPTION_STATUS } from 'utils/prescription';
import { DATE_FORMATS } from 'utils/dateTime';
import { IProps } from './types';
import styles from './styles.module.scss';

/**
 * Allows the user to select an access right from a dropdown.
 * @param props The props
 * @return a JSX Element
 */
function AccessRightSelector(props: IProps): JSX.Element {
  const {
    accessRights,
    selectedArId,
    setSelectedArId,
    setSelectedAccessRight,
    setOverview,
    setRewardList,
    setUsageReport,
    setCurriculumProgress,
  } = props;

  /**
   * Returns a dropdown label for the given access right.
   * @param ar The access right
   * @return A label for the given access right
   */
  function getLabel(ar: AccessRightDTO): string {
    let start = moment(ar.startDate);
    let end = moment(ar.endDate);
    if (ar.timezone) {
      start = start.tz(ar.timezone);
      end = end.tz(ar.timezone);
    }
    switch (ar.status) {
      case PRESCRIPTION_STATUS.STARTED:
        return `Current (Started on ${start.format(DATE_FORMATS.M_D_YYYY)})`;
      case PRESCRIPTION_STATUS.EXPIRED:
        return `${start.format(DATE_FORMATS.M_D_YYYY)} - ${end.format(
          DATE_FORMATS.M_D_YYYY,
        )}`;
      default:
        return 'Not Started';
    }
  }

  function updateAccessRight(id: string): void {
    setSelectedArId(id);
    setSelectedAccessRight(accessRights.find((ar) => ar.id === id)!);

    // Clear caches
    setOverview(undefined);
    setRewardList(undefined);
    setUsageReport(undefined);
    setCurriculumProgress(undefined);
  }

  const options = accessRights.map((ar) => ({
    value: ar.id,
    label: getLabel(ar),
    disabled: ar.id === selectedArId,
  }));
  const selectedOption = options.find((ar) => ar.value === selectedArId);
  const program = accessRights
    .find((ar) => ar.id === selectedArId)
    ?.program?.toLowerCase();

  return (
    <div data-testid="access-right-selector" className={styles.wrapper}>
      <div className={styles.selectorWrapper}>
        <div className={styles.label}>Prescription:</div>
        <div
          className={styles.selector}
          aria-label="Prescription Dropdown Button"
        >
          <Select
            disableSorting
            options={options}
            selectedOption={selectedOption}
            onChange={(e) => updateAccessRight(e.value)}
          />
        </div>
      </div>
      <div
        className={styles[`${program}-logo`]}
        aria-label={program}
        data-testid="program-logo"
      />
    </div>
  );
}

export default AccessRightSelector;
